import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useAuthStore } from "@/stores/auth-store";

// API configuration
const API_BASE_URL =
  (import.meta as any).env?.VITE_API_URL || "http://localhost:3001/api";

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshToken = useAuthStore.getState().refreshToken;
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;

          // Update store with new tokens
          useAuthStore.getState().setTokens(accessToken, newRefreshToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        useAuthStore.getState().logout();
        window.location.href = "/auth/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API response wrapper
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

// Generic API methods
export const apiClient = {
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> =>
    api.get<ApiResponse<T>>(url, config).then((res) => res.data),

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> =>
    api.post<ApiResponse<T>>(url, data, config).then((res) => res.data),

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> =>
    api.put<ApiResponse<T>>(url, data, config).then((res) => res.data),

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> =>
    api.patch<ApiResponse<T>>(url, data, config).then((res) => res.data),

  delete: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> =>
    api.delete<ApiResponse<T>>(url, config).then((res) => res.data),

  // File upload helper
  uploadFile: <T = unknown>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> => {
    const formData = new FormData();
    formData.append("file", file);

    return api
      .post<ApiResponse<T>>(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(progress);
          }
        },
      })
      .then((res) => res.data);
  },

  // Download file helper
  downloadFile: (url: string, filename?: string): Promise<void> => {
    return api
      .get(url, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      });
  },
};

// Typed API endpoints
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    apiClient.post("/auth/login", credentials),

  signup: (userData: { email: string; password: string; name?: string }) =>
    apiClient.post("/auth/signup", userData),

  logout: () => apiClient.post("/auth/logout"),

  refreshToken: (refreshToken: string) =>
    apiClient.post("/auth/refresh", { refreshToken }),

  getProfile: () => apiClient.get("/auth/profile"),

  verifyToken: () => apiClient.get("/auth/verify"),

  googleAuth: (code: string) => apiClient.post("/auth/google", { code }),

  getGoogleAuthUrl: () => apiClient.get("/auth/google/url"),
};

export const teamsAPI = {
  getTeams: () => apiClient.get("/teams"),

  createTeam: (teamData: {
    name: string;
    industry?: string | undefined;
    description?: string | undefined;
    website?: string | undefined;
    linkedinUrl?: string | undefined;
    companySize?: string | undefined;
    minBudget?: number | undefined;
    maxBudget?: number | undefined;
    services?: string[] | undefined;
    targetMarkets?: string[] | undefined;
  }) => apiClient.post("/teams", teamData),

  getTeam: (id: string) => apiClient.get(`/teams/${id}`),

  updateTeam: (
    id: string,
    teamData: Partial<{ name: string; industry: string }>
  ) => apiClient.patch(`/teams/${id}`, teamData),

  deleteTeam: (id: string) => apiClient.delete(`/teams/${id}`),

  inviteMember: (teamId: string, email: string, role: string) =>
    apiClient.post(`/teams/${teamId}/invite`, { email, role }),

  getMembers: (teamId: string) => apiClient.get(`/teams/${teamId}/members`),

  updateMember: (teamId: string, userId: string, data: { role: string }) =>
    apiClient.patch(`/teams/${teamId}/members/${userId}`, data),

  removeMember: (teamId: string, userId: string) =>
    apiClient.delete(`/teams/${teamId}/members/${userId}`),
};

export const leadsAPI = {
  getLeads: (teamId: number, params?: {
    page?: number;
    limit?: number;
    status?: string;
    source?: string;
    industry?: string;
    companySize?: string;
    minBudget?: number;
    maxBudget?: number;
    search?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => apiClient.get("/leads", { params: { teamId, ...params } }),

  createLead: (teamId: number, leadData: {
    email: string;
    name?: string | undefined;
    company?: string | undefined;
    jobTitle?: string | undefined;
    phone?: string | undefined;
    website?: string | undefined;
    linkedinUrl?: string | undefined;
    industry?: string | undefined;
    companySize?: string | undefined;
    budgetEstimate?: number | undefined;
    location?: string | undefined;
    source?: string | undefined;
    notes?: string | undefined;
    tags?: string[] | undefined;
  }) => apiClient.post("/leads", leadData, { params: { teamId } }),

  getLead: (teamId: number, leadId: number) => 
    apiClient.get(`/leads/${leadId}`, { params: { teamId } }),

  updateLead: (teamId: number, leadId: number, leadData: Partial<any>) =>
    apiClient.put(`/leads/${leadId}`, leadData, { params: { teamId } }),

  deleteLead: (teamId: number, leadId: number) => 
    apiClient.delete(`/leads/${leadId}`, { params: { teamId } }),

  importLeads: (teamId: number, file: File, options?: {
    skipHeader?: boolean;
    delimiter?: string;
    overrideExisting?: boolean;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.skipHeader !== undefined) formData.append('skipHeader', options.skipHeader.toString());
    if (options?.delimiter) formData.append('delimiter', options.delimiter);
    if (options?.overrideExisting !== undefined) formData.append('overrideExisting', options.overrideExisting.toString());
    
    return api.post(`/leads/import?teamId=${teamId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  enrichLead: (teamId: number, leadId: number) => 
    apiClient.post(`/leads/${leadId}/enrich`, {}, { params: { teamId } }),

  exportLeads: (teamId: number, params?: any, format: "csv" = "csv") =>
    apiClient.downloadFile(`/leads/export?teamId=${teamId}&format=${format}`, `leads.${format}`),

  getStats: (teamId: number) => 
    apiClient.get("/leads/stats", { params: { teamId } }),
};

export const campaignsAPI = {
  getCampaigns: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    templateId?: number;
    createdBy?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => apiClient.get("/campaigns", { params }),

  createCampaign: (campaignData: {
    name: string;
    description?: string;
    campaignType: string;
    targetIndustries?: string[];
    templateId?: number;
    targetLeadIds: number[];
    sendSettings?: {
      dailyLimit?: number;
      sendInterval?: number;
      businessHoursOnly?: boolean;
      startTime?: string;
      endTime?: string;
      timezone?: string;
      skipWeekends?: boolean;
    };
    aiPersonalization?: boolean;
    scheduledFor?: string;
    customSubject?: string;
    customContent?: string;
  }) => apiClient.post("/campaigns", campaignData),

  getCampaign: (id: number) => apiClient.get(`/campaigns/${id}`),

  updateCampaign: (id: number, campaignData: {
    name?: string;
    description?: string;
    status?: string;
    templateId?: number;
    targetLeadIds?: number[];
    sendSettings?: any;
    aiPersonalization?: boolean;
    scheduledFor?: string;
  }) => apiClient.put(`/campaigns/${id}`, campaignData),

  deleteCampaign: (id: number) => apiClient.delete(`/campaigns/${id}`),

  getCampaignStats: (id: number) => apiClient.get(`/campaigns/${id}/stats`),

  launchCampaign: (id: number) => apiClient.post(`/campaigns/${id}/launch`),

  startCampaign: (id: number) => apiClient.post(`/campaigns/${id}/start`),

  pauseCampaign: (id: number) => apiClient.post(`/campaigns/${id}/pause`),

  cancelCampaign: (id: number) => apiClient.post(`/campaigns/${id}/cancel`),

  getMatchingLeads: (matchingData: {
    campaignType: string;
    targetIndustries?: string[];
    minMatchScore?: number;
    limit?: number;
  }) => apiClient.post("/campaigns/matching-leads", matchingData),
};

export const followUpAPI = {
  getFollowUps: (params?: { page?: number; limit?: number; status?: string }) =>
    apiClient.get("/follow-up", { params }),

  getSuggestions: (params?: {
    page?: number;
    limit?: number;
    priority?: string;
  }) => apiClient.get("/follow-up/suggestions", { params }),

  approveSuggestion: (id: string) =>
    apiClient.post(`/follow-up/suggestions/${id}/approve`),

  rejectSuggestion: (id: string, reason?: string) =>
    apiClient.post(`/follow-up/suggestions/${id}/reject`, { reason }),

  getSequences: (leadId?: string) =>
    apiClient.get("/follow-up/sequences", {
      params: leadId ? { leadId } : undefined,
    }),

  createSequence: (sequenceData: {
    leadId: string;
    steps: Array<{ content: string; delayDays: number }>;
  }) => apiClient.post("/follow-up/sequences", sequenceData),
};

export const analyticsAPI = {
  getOverview: (timeframe?: "7d" | "30d" | "90d") =>
    apiClient.get("/analytics/overview", { params: { timeframe } }),

  getLeadMetrics: (timeframe?: "7d" | "30d" | "90d") =>
    apiClient.get("/analytics/leads", { params: { timeframe } }),

  getCampaignMetrics: (timeframe?: "7d" | "30d" | "90d") =>
    apiClient.get("/analytics/campaigns", { params: { timeframe } }),

  getRecentActivity: (limit?: number) =>
    apiClient.get("/analytics/activity", { params: { limit } }),

  getPerformanceReport: (startDate: string, endDate: string) =>
    apiClient.get("/analytics/performance", { params: { startDate, endDate } }),
};

// Email Templates API
import { 
  CreateEmailTemplateData, 
  UpdateEmailTemplateData, 
  GenerateEmailContentData,
  EmailTemplateFilters,
  EmailTemplateType 
} from '@/types/email-templates';

export const emailTemplatesAPI = {
  getTemplates: (teamId: number, filters?: EmailTemplateFilters) => 
    apiClient.get(`/teams/${teamId}/email-templates`, { params: filters }),
  
  getTemplate: (teamId: number, templateId: number) => 
    apiClient.get(`/teams/${teamId}/email-templates/${templateId}`),
  
  createTemplate: (teamId: number, templateData: CreateEmailTemplateData) => 
    apiClient.post(`/teams/${teamId}/email-templates`, templateData),
  
  updateTemplate: (teamId: number, templateId: number, templateData: UpdateEmailTemplateData) => 
    apiClient.put(`/teams/${teamId}/email-templates/${templateId}`, templateData),
  
  deleteTemplate: (teamId: number, templateId: number) => 
    apiClient.delete(`/teams/${teamId}/email-templates/${templateId}`),
  
  generateContent: (teamId: number, generateData: GenerateEmailContentData) => 
    apiClient.post(`/teams/${teamId}/email-templates/generate`, generateData),
  
  getTemplateTypes: () => 
    apiClient.get('/teams/1/email-templates/types/available'), // Using dummy team ID for types endpoint
};

// Email Accounts API
export const emailAccountsAPI = {
  getAccounts: () => 
    apiClient.get('/email/accounts'),
  
  getGmailAuthUrl: () => 
    apiClient.post('/email/accounts/gmail/connect'),
  
  handleGmailCallback: (callbackData: { code: string; state?: string }) => 
    apiClient.post('/email/accounts/gmail/callback', callbackData),
  
  disconnectAccount: (provider: string) => 
    apiClient.delete(`/email/accounts/${provider}`),
  
  getStatus: () => 
    apiClient.get('/email/status'),
};

// Intelligent Discovery API
export const discoveryAPI = {
  // Session Management
  createSession: (teamId: number, sessionData: {
    name: string;
    description?: string;
    discoveryConfig: {
      targetCount?: number;
      industries?: string[];
      companySize?: string[];
      services?: string[];
      geography?: string[];
      customCriteria?: string;
    };
  }) => apiClient.post(`/intelligent-discovery/sessions?teamId=${teamId}`, sessionData),

  getSessions: (teamId: number, params?: {
    limit?: number;
    offset?: number;
  }) => apiClient.get(`/intelligent-discovery/sessions?teamId=${teamId}`, { params }),

  getActiveSession: (teamId: number) => 
    apiClient.get(`/intelligent-discovery/sessions/active?teamId=${teamId}`),

  getSession: (teamId: number, sessionId: number) => 
    apiClient.get(`/intelligent-discovery/sessions/${sessionId}?teamId=${teamId}`),

  resumeSession: (teamId: number, sessionId: number) => 
    apiClient.post(`/intelligent-discovery/sessions/${sessionId}/resume?teamId=${teamId}`),

  pauseSession: (teamId: number, sessionId: number) => 
    apiClient.post(`/intelligent-discovery/sessions/${sessionId}/pause?teamId=${teamId}`),

  getSessionLeads: (teamId: number, sessionId: number, params?: {
    minScore?: number;
    maxScore?: number;
    source?: string;
    isSelected?: boolean;
    isImported?: boolean;
    limit?: number;
    offset?: number;
  }) => apiClient.get(`/intelligent-discovery/sessions/${sessionId}/leads?teamId=${teamId}`, { params }),

  updateLeadSelection: (teamId: number, sessionId: number, data: {
    leadIds: number[];
    isSelected: boolean;
  }) => apiClient.post(`/intelligent-discovery/sessions/${sessionId}/leads/select?teamId=${teamId}`, data),

  importSelectedLeads: (teamId: number, sessionId: number) => 
    apiClient.post(`/intelligent-discovery/sessions/${sessionId}/import?teamId=${teamId}`),

  getSessionStats: (teamId: number, sessionId: number) => 
    apiClient.get(`/intelligent-discovery/sessions/${sessionId}/stats?teamId=${teamId}`),
    
  getSessionProgress: (teamId: number, sessionId: number) => 
    apiClient.get(`/intelligent-discovery/sessions/${sessionId}/progress?teamId=${teamId}`),

  // Legacy Job-based API (deprecated but kept for backwards compatibility)
  startDiscovery: (teamId: number, discoveryData: {
    targetCount?: number;
    industries?: string[];
    companySize?: string[];
    services?: string[];
    geography?: string[];
    customCriteria?: string;
  }) => apiClient.post(`/intelligent-discovery/start?teamId=${teamId}`, discoveryData),

  getJobStatus: (teamId: number, jobId: number) => 
    apiClient.get(`/intelligent-discovery/jobs/${jobId}/status?teamId=${teamId}`),

  getDiscoveredLeads: (teamId: number, jobId: number, params?: {
    minScore?: number;
    limit?: number;
    includeAnalysis?: boolean;
  }) => apiClient.get(`/intelligent-discovery/jobs/${jobId}/leads?teamId=${teamId}`, { params }),

  createCampaignFromDiscovery: (teamId: number, jobId: number, campaignData: {
    campaignName: string;
    minLeadScore?: number;
    maxLeads?: number;
    templateId?: number;
    description?: string;
  }) => apiClient.post(`/intelligent-discovery/jobs/${jobId}/create-campaign?teamId=${teamId}`, campaignData),

  // General API methods
  getDiscoveryStats: (teamId: number) => 
    apiClient.get(`/intelligent-discovery/stats?teamId=${teamId}`),

  getDiscoveryExamples: () => 
    apiClient.get('/intelligent-discovery/examples'),

  healthCheck: () => 
    apiClient.get('/intelligent-discovery/health'),
};

export default api;
